import { useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, BarChart, Bar, Cell } from "recharts";
import { Sparkles, Flame, TrendingDown, Sprout } from "lucide-react";
import { Btn } from "@/components/Btn";
import { Spinner } from "@/components/Brand";
import { FOUR_F, moodColor, moodLevel } from "@/lib/config";
import { flipStats, schemaScript } from "@/lib/schemaFlip";
import { overrideTrend } from "@/lib/overrideTrend";
import { healthyWins } from "@/lib/healthyWins";
import { aiPost } from "@/lib/store";
import { NoteHeader } from "@/components/AiNote";
import { allNeedLists, latestIdeas, needStats } from "@/lib/needs";
import { STATE_HEX } from "@/lib/theme";
import { RecoveryTime, TriggerMoodTrends } from "@/components/TriggerTrends";
import { WeeklySummary } from "@/components/WeeklySummary";
import { ListenButton } from "@/components/AiNote";
import { insightsSpeech } from "@/lib/insightsSpeech";
import { usePrefs } from "@/lib/store";

const count = (arr) => Object.entries(arr.reduce((m, k) => ((m[k] = (m[k] || 0) + 1), m), {})).sort((a, b) => b[1] - a[1]);
const Card = ({ title, children, testId }) => (
  <div className="tf-card p-5" data-testid={testId}><p className="font-bold text-t-on mb-3">{title}</p>{children}</div>
);
const Bars = ({ rows, empty, color = "var(--brand)" }) =>
  rows.length ? (
    <div className="flex flex-col gap-2">
      {rows.slice(0, 6).map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-sm"><span className="text-t-on2">{k}</span><span className="font-semibold text-t-on">{v}</span></div>
          <div className="h-2 rounded-full bg-t-surface3 mt-1"><div className="h-2 rounded-full" style={{ width: `${(v / rows[0][1]) * 100}%`, background: color }} /></div>
        </div>
      ))}
    </div>
  ) : <p className="text-sm text-t-muted">{empty}</p>;

function moodStreak(sessions) {
  const days = new Set(sessions.filter((s) => s.moodNow != null && s.moodNow >= 0).map((s) => s.timestamp.slice(0, 10)));
  let n = 0;
  const d = new Date();
  if (!days.has(d.toISOString().slice(0, 10))) d.setDate(d.getDate() - 1);
  while (days.has(d.toISOString().slice(0, 10))) { n += 1; d.setDate(d.getDate() - 1); }
  return n;
}

function GrowthStory({ sessions, flips }) {
  const [story, setStory] = useState("");
  const [loading, setLoading] = useState(false);
  const make = async () => {
    setLoading(true);
    const month = new Date().toISOString().slice(0, 7);
    const mf = flips.filter((f) => f.timestamp.startsWith(month));
    const st = flipStats(mf);
    const lifts = sessions.filter((s) => s.timestamp.startsWith(month) && s.moodBefore != null && s.moodNow != null).map((s) => s.moodNow - s.moodBefore);
    setStory(await aiPost("growth-story", {
      month, flips: st.total, topSchemas: st.topSchemas.map((x) => x[0]), adaptive: st.topSchemas.map((x) => schemaScript(x[0])?.adaptive),
      copingStyles: st.styles, actionsDone: st.done, actionsTotal: st.actions, checkins: sessions.filter((s) => s.timestamp.startsWith(month)).length,
      avgMoodLift: lifts.length ? lifts.reduce((a, b) => a + b, 0) / lifts.length : null,
      ownScripts: mf.flatMap((f) => f.flips.map((x) => x.ownScript).filter(Boolean)),
    }, "story"));
    setLoading(false);
  };
  return (
    <div className="rounded-[20px] bg-t-brand3 p-5" data-testid="growth-story">
      <NoteHeader title={<p className="flex items-center gap-2 font-bold text-t-onbrand3"><Sparkles size={17} /> Your growth story this month</p>} text={loading ? null : story} testId="growth-story-listen" />
      {story ? <p className="mt-3 text-t-on leading-relaxed whitespace-pre-line" data-testid="growth-story-text">{story}</p> : null}
      <Btn size="md" variant={story ? "ghost" : "primary"} className="mt-3" onClick={make} disabled={loading} testId="growth-story-button"
        label={loading ? "Writing your story…" : story ? "Write it again" : "Write my growth story"} icon={loading ? <Spinner className="w-4 h-4" /> : null} />
    </div>
  );
}

export function HistoryInsights({ sessions, flips }) {
  const { data: prefs } = usePrefs();
  const data = useMemo(() => {
    const asc = [...sessions].reverse();
    const mood = asc.filter((s) => s.moodNow != null).slice(-30).map((s) => ({ d: new Date(s.timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short" }), v: s.moodNow }));
    const drops = asc.filter((s) => s.postSuds != null).slice(-14).map((s) => ({ d: new Date(s.timestamp).toLocaleDateString(undefined, { day: "numeric", month: "short" }), v: s.preSuds - s.postSuds }));
    const four = FOUR_F.map((f) => [f.label, sessions.filter((s) => s.survivalResponse === f.key).length, f.key]).filter((x) => x[1]);
    const coping = count(sessions.flatMap((s) => s.copingMechanisms || []));
    const lift = {};
    sessions.filter((s) => s.moodBefore != null && s.moodNow != null).forEach((s) => (s.copingMechanisms || []).forEach((c) => (lift[c] = [...(lift[c] || []), s.moodNow - s.moodBefore])));
    const liftRows = Object.entries(lift).map(([k, v]) => [k, Math.round((v.reduce((a, b) => a + b, 0) / v.length) * 10) / 10]).filter((x) => x[1] > 0).sort((a, b) => b[1] - a[1]);
    const parts = { Mornings: [5, 12], Afternoons: [12, 17], Evenings: [17, 21], "Late at night": [21, 29] };
    const calm = Object.entries(parts).map(([k, [a, b]]) => {
      const inPart = sessions.filter((s) => s.preSuds != null).filter((s) => { const h = new Date(s.timestamp).getHours(); const hh = h < 5 ? h + 24 : h; return hh >= a && hh < b; });
      return [k, inPart.length ? inPart.reduce((x, s) => x + s.preSuds, 0) / inPart.length : null, inPart.length];
    }).filter((x) => x[2] >= 2).sort((a, b) => a[1] - b[1]);
    return {
      mood, drops, four, coping, liftRows, calm,
      schemas: count(sessions.map((s) => s.schemaState).filter(Boolean)),
      modes: count(sessions.map((s) => s.copingMode).filter(Boolean)),
      ems: count(sessions.flatMap((s) => s.emaSchemas || [])),
      triggers: count(sessions.filter((s) => s.triggerNote).map((s) => s.triggerNote.toLowerCase().trim())).filter((x) => x[1] > 1),
      triggered: sessions.filter((s) => s.triggered).length,
      streak: moodStreak(sessions),
      wins: healthyWins(flips),
      trend: overrideTrend(sessions),
      flip: flipStats(flips),
      needs: needStats(allNeedLists(sessions, flips)),
      ideas: latestIdeas(sessions, flips),
    };
  }, [sessions, flips]);

  if (!sessions.length && !flips.length) return <div className="tf-card p-10 text-center text-t-muted" data-testid="insights-empty">Insights appear after your first few check-ins.</div>;

  return (
    <div className="flex flex-col gap-4" data-testid="history-insights">
      <div className="flex items-center gap-3"><ListenButton text={insightsSpeech(sessions, data, prefs?.weeklySummary)} testId="insights-listen" /><span className="text-sm text-t-muted">Hear a calm summary of your insights</span></div>
      <WeeklySummary />
      <div className="grid grid-cols-3 gap-3">
        {[[data.streak, "Mood streak", Flame], [data.triggered, "Triggers logged", TrendingDown], [data.wins.total, "Healthy Adult wins", Sprout]].map(([n, l, Icon]) => (
          <div key={l} className="tf-card p-4"><Icon size={18} className="text-t-brand" /><p className="text-3xl font-bold text-t-on mt-2">{n}</p><p className="text-xs text-t-muted">{l}</p></div>
        ))}
      </div>
      <GrowthStory sessions={sessions} flips={flips} />
      <Card title="Mood over time" testId="insight-mood">
        {data.mood.length > 1 ? (
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={data.mood} margin={{ left: -24, right: 8, top: 8 }}>
                <XAxis dataKey="d" tick={{ fontSize: 11, fill: "var(--muted)" }} />
                <YAxis domain={[-10, 10]} tick={{ fontSize: 11, fill: "var(--muted)" }} />
                <ReferenceLine y={0} stroke="var(--border2)" strokeDasharray="4 4" />
                <Tooltip formatter={(v) => [`${moodLevel(v)?.label} (${v})`, "Mood"]} contentStyle={{ borderRadius: 12 }} />
                <Line dataKey="v" stroke="var(--brand)" strokeWidth={3} dot={({ cx, cy, payload }) => <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={5} fill={moodColor(payload.v)} />} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-sm text-t-muted">Log a couple of moods to see your trend · middle = grounded.</p>}
      </Card>
      <Card title="Distress drops" testId="insight-drops">
        {data.drops.length ? (
          <div className="h-44">
            <ResponsiveContainer>
              <BarChart data={data.drops} margin={{ left: -24, right: 8, top: 8 }}>
                <XAxis dataKey="d" tick={{ fontSize: 11, fill: "var(--muted)" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "var(--muted)" }} />
                <Tooltip formatter={(v) => [`${v} points`, "Drop"]} contentStyle={{ borderRadius: 12 }} />
                <Bar dataKey="v" radius={[8, 8, 0, 0]}>{data.drops.map((d, i) => <Cell key={i} fill={d.v > 0 ? "var(--success)" : "var(--border2)"} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : <p className="text-sm text-t-muted">Use Get help now to see how much your distress settles.</p>}
        {data.trend.message ? <p className="mt-3 text-sm text-t-on2">{data.trend.message}</p> : null}
      </Card>
      <TriggerMoodTrends sessions={sessions} />
      <RecoveryTime sessions={sessions} />
      <div className="grid sm:grid-cols-2 gap-4">
        <Card title="Survival responses" testId="insight-fourf">
          {data.four.length ? data.four.map(([l, n, k]) => (
            <div key={k} className="flex items-center gap-2 py-1"><span className="w-3 h-3 rounded-full" style={{ background: STATE_HEX[k] }} /><span className="flex-1 text-t-on2">{l}</span><b className="text-t-on">{n}</b></div>
          )) : <p className="text-sm text-t-muted">None identified yet.</p>}
        </Card>
        <Card title="Calmest time of day" testId="insight-calm">
          {data.calm.length ? <p className="text-t-on"><b>{data.calm[0][0]}</b> — average distress {data.calm[0][1].toFixed(1)}/10</p> : <p className="text-sm text-t-muted">Not enough check-ins yet to spot your calmest time.</p>}
        </Card>
      </div>
      <Card title="Most-used coping" testId="insight-coping"><Bars rows={data.coping} empty="Tag what helped at the end of a check-in." /></Card>
      <Card title="Tools with the biggest mood lift" testId="insight-lift"><Bars rows={data.liftRows} color="var(--success)" empty="Log your mood after the trigger and after coping to discover what lifts you." /></Card>
      <div className="grid sm:grid-cols-2 gap-4">
        <Card title="Coping modes activated most" testId="insight-modes"><Bars rows={data.modes} empty="None yet." /></Card>
        <Card title="Schema states" testId="insight-schemas"><Bars rows={data.schemas} empty="None yet." /></Card>
      </div>
      <Card title="Schemas activated most" testId="insight-ems"><Bars rows={[...data.ems, ...data.flip.topSchemas.filter(([k]) => !data.ems.some(([e]) => e === k))]} empty="Recognise schemas in Flip the Script to see patterns." /></Card>
      <Card title="Core needs that come up most" testId="insight-needs">
        {data.needs.total ? (
          <>
            <p className="text-sm text-t-on2 mb-3">{data.needs.groups.map(([l, n]) => `${l}: ${n}`).join(" · ")}</p>
            <Bars rows={data.needs.items} color="#E0A27C" empty="" />
            {data.ideas.length ? (
              <div className="mt-4" data-testid="insight-need-ideas">
                <p className="tf-tag mb-2">Your recent ideas for meeting them</p>
                {data.ideas.map((v) => (
                  <div key={v.need} className="py-2 border-t border-t-border first:border-0">
                    <p className="text-sm font-semibold text-t-on">{v.need}</p>
                    {v.ideas.map((i) => <p key={i} className="text-sm text-t-on2">• {i}</p>)}
                  </div>
                ))}
              </div>
            ) : null}
          </>
        ) : <p className="text-sm text-t-muted">Name your unmet needs during a reflection to see which come up most.</p>}
      </Card>
      <Card title="Recurring triggers" testId="insight-triggers"><Bars rows={data.triggers} color="var(--hot)" empty="Repeated trigger notes will show here." /></Card>
    </div>
  );
}
